import { ConversationParticipantEntity } from '@provision/conversation-types';
import { ColoredAvatar, PlatformUserAvatar } from 'src/components';
import { OptionHint, OptionRow } from 'src/components/SelectMenu/select-menu.elements';

function highlightMatch(baseString: string, searchString: string | undefined) {
  if (!baseString) return '';
  if (!searchString) return baseString;

  // Convert both baseString and searchString to lowercase for case-insensitive matching
  const lowerBaseString = baseString.toLowerCase();
  const lowerSearchString = searchString.toLowerCase();

  const index = lowerBaseString.indexOf(lowerSearchString);
  if (index === -1) {
    // Return the base string unchanged if search string is not found
    return baseString;
  }

  const beforeMatch = baseString.substring(0, index);
  const match = baseString.substring(index, index + searchString.length);
  const afterMatch = baseString.substring(index + searchString.length);

  return (
    <>
      {beforeMatch}
      <b>{match}</b>
      {afterMatch}
    </>
  );
}

interface RequesterListItemProps {
  id: string;
  props?: React.HTMLAttributes<HTMLLIElement>;
  firstLine: string;
  secondLine?: string;
  inputValue?: string;
  externalPartnerId?: number;
  participantEntity?: ConversationParticipantEntity;
  entityPartnerId?: number;
  avatarFileKey?: string | null;
}

export const RequesterListItem = ({
  id,
  props,
  firstLine,
  secondLine = '',
  inputValue,
  externalPartnerId,
  participantEntity,
  entityPartnerId,
  avatarFileKey,
}: RequesterListItemProps) => {
  const isPlatformUser = participantEntity === ConversationParticipantEntity.PlatformUser;
  const isClientWithoutEmail =
    !secondLine && participantEntity === ConversationParticipantEntity.Client;

  return (
    // Every row has a second line, so content top-aligns like SelectMenu's described rows.
    <li {...props} key={id} style={{ alignItems: 'flex-start' }}>
      <OptionRow
        avatar={
          isPlatformUser ? (
            <PlatformUserAvatar
              partnerId={entityPartnerId}
              avatarFileKey={avatarFileKey}
              name={firstLine}
            />
          ) : (
            <ColoredAvatar name={firstLine} />
          )
        }
        title={highlightMatch(firstLine, inputValue)}
        titleEnd={
          externalPartnerId ? (
            <OptionHint>{`Workspace: ${externalPartnerId}`}</OptionHint>
          ) : null
        }
        description={
          isClientWithoutEmail ? (
            <em>No email address on client record</em>
          ) : (
            highlightMatch(secondLine, inputValue)
          )
        }
      />
    </li>
  );
};
